import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { supabase, signOut } from './supabase';
import { allowedDomains } from './env';
import { emailDomain } from './format';
import type { TeamMember } from './queries';

interface AuthState {
  session: Session | null;
  teamMember: TeamMember | null;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

// localStorage key for the last successfully-validated team_member row.
// Hydrating from this lets the second+ visit render the full app on the
// FIRST paint instead of staring at "Checking sign-in…" while Supabase's
// `getSession()` + the `team_members` SELECT round-trip. The background
// re-validation still runs and overwrites the cache; if the cached row is
// stale (deactivated, email changed) the user gets bounced to /login.
//
// Schema discipline: any TeamMember field used by the UI must be present
// on the cached row or you'll see `undefined`-related glitches on the
// first frame after a cache hit. Bump CACHE_VERSION when the consumer
// shape changes — the loader rejects mismatched versions so old caches
// don't ship malformed rows.
// v2 (migration 078): the row gained workspace_role_id + access_expires_at,
// and permissions come from my_access, never from this cached row.
// (The storage key stays -v1: signOut() in supabase.ts clears it by name.)
const TM_CACHE_KEY = 'pa-auth-tm-cache-v1';
const CACHE_VERSION = 2;

interface CachedTeamMember {
  v: number;
  email: string; // sanity check — must match the live session
  tm: TeamMember;
  at: number; // epoch ms, for staleness debugging
}

function loadCachedTeamMember(): CachedTeamMember | null {
  try {
    const raw = localStorage.getItem(TM_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedTeamMember;
    if (parsed?.v !== CACHE_VERSION || !parsed.email || !parsed.tm) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveCachedTeamMember(tm: TeamMember) {
  try {
    const payload: CachedTeamMember = {
      v: CACHE_VERSION,
      email: tm.email.toLowerCase(),
      tm,
      at: Date.now(),
    };
    localStorage.setItem(TM_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // Quota / private mode / disabled storage — caching is best-effort.
  }
}

export function clearCachedTeamMember() {
  try {
    localStorage.removeItem(TM_CACHE_KEY);
  } catch {
    // ignore
  }
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Hydrate from the localStorage cache so the SECOND+ visit renders the
  // app on first paint — no "Checking sign-in…" splash. The background
  // session check + team_members re-fetch still runs; if the cached row
  // doesn't match the live session (deactivated, email changed) the
  // validation effect below overwrites it and may sign the user out.
  const cachedOnInit = typeof window !== 'undefined' ? loadCachedTeamMember() : null;

  const [session, setSession] = useState<Session | null>(null);
  const [teamMember, setTeamMember] = useState<TeamMember | null>(
    cachedOnInit?.tm ?? null,
  );
  const [sessionLoading, setSessionLoading] = useState(true);
  // Start validation as `false` when we have a cached team_member — the
  // user already sees a fully-rendered app. We'll flip `validating=true`
  // inside the validate effect only when the cached email DOESN'T match
  // the freshly-resolved session (i.e. a different user logged in
  // elsewhere). For a new visitor with no cache, this stays true.
  const [validating, setValidating] = useState(!cachedOnInit);
  const [error, setError] = useState<string | null>(null);

  // Mirror teamMember in a ref so the validation effect can read the
  // current value without having to declare teamMember as a dep (which
  // would create an infinite re-validate loop).
  const teamMemberRef = useRef<TeamMember | null>(cachedOnInit?.tm ?? null);
  useEffect(() => {
    teamMemberRef.current = teamMember;
  });

  // 1. Track the Supabase session.
  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setSessionLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setSessionLoading(false);
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // 2. Whenever the session changes, validate domain + team_members membership.
  // We keep `validating=true` until this resolves so the auth gate doesn't
  // bounce the user mid-hydration on a hard refresh.
  //
  // Tab-switch quirk: Supabase fires a session-update event (often
  // TOKEN_REFRESHED) when the tab regains focus, which used to re-run
  // the whole validation flow — RequireAuth flipped to "Checking
  // sign-in…", unmounting every page below and resetting scroll. Now
  // we short-circuit when the new session is for the SAME user we
  // already validated; the in-memory teamMember + auth state stays
  // intact, no re-render, no scroll jump.
  useEffect(() => {
    let active = true;
    async function validate() {
      setError(null);

      // Wait for the initial session fetch to settle before deciding anything.
      if (sessionLoading) return;

      if (!session?.user?.email) {
        setTeamMember(null);
        clearCachedTeamMember();
        setValidating(false);
        return;
      }

      const email = session.user.email.toLowerCase();

      // Same-user fast path: if we already validated this email, this is
      // a token refresh or a redundant session-update event. Skip the
      // DB hit + the loading flash. Do NOT call setValidating(true) above
      // — only set it for new/different users.
      const cached = teamMemberRef.current;
      if (cached && cached.email.toLowerCase() === email) {
        setValidating(false);
        return;
      }

      setValidating(true);
      const domain = emailDomain(email);
      const allowed = allowedDomains();
      if (!domain || (allowed.length > 0 && !allowed.includes(domain))) {
        setError(`Sign-in restricted: ${email} is not on an allowed Workspace domain.`);
        setTeamMember(null);
        setValidating(false);
        await signOut();
        return;
      }

      const { data, error: dbErr } = await supabase
        .from('team_members')
        .select('*')
        .eq('email', email)
        .eq('active', true)
        .maybeSingle();

      if (!active) return;

      if (dbErr) {
        setError(`Could not verify team membership: ${dbErr.message}`);
        setTeamMember(null);
        clearCachedTeamMember();
        setValidating(false);
        return;
      }
      if (!data) {
        // This is the first thing a new hire sees if they sign in before an
        // admin has added them. It used to name a Postgres table.
        setError(
          `${email} isn't set up on the support team yet. Ask an admin to add you, then sign in again.`,
        );
        setTeamMember(null);
        clearCachedTeamMember();
        setValidating(false);
        await signOut();
        return;
      }

      // First-login bookkeeping (migration 035). The status badge on the
      // Team page derives from joined_at — null = "Pending invitation",
      // non-null = active. We stamp it the first time auth-guard sees
      // this user authenticate successfully. Fire-and-forget; failure
      // doesn't block the user from getting in, it just means the badge
      // doesn't flip yet.
      const tm = data as TeamMember;
      if (!tm.joined_at) {
        const now = new Date().toISOString();
        void supabase
          .from('team_members')
          .update({ joined_at: now })
          .eq('email', email)
          .then(() => {});
        tm.joined_at = now;
      }

      // Self-heal avatar_url from the user's Google OAuth metadata. The
      // sync-team-avatars.ts script populates this from Slack via
      // `users.lookupByEmail`, but that fails for any team_member who
      // isn't in the Slack workspace under their Google email — they end
      // up with no avatar despite signing in via Google. Google OAuth
      // returns the user's profile picture in user_metadata.avatar_url
      // (or .picture); copy that across when ours is null/empty so every
      // signed-in user gets a real avatar without admin intervention.
      const meta = (session.user.user_metadata ?? {}) as {
        avatar_url?: string;
        picture?: string;
      };
      const googleAvatar = meta.avatar_url || meta.picture || '';
      if (googleAvatar && !tm.avatar_url) {
        // Fire and forget — the SPA can use the Google avatar from session
        // immediately, and we propagate to team_members so other teammates
        // see it on the dashboard / activity sidebar / etc.
        void supabase
          .from('team_members')
          .update({ avatar_url: googleAvatar })
          .eq('email', email)
          .then(() => {
            // optional: log success
          });
        // Optimistically reflect the avatar in local state so this user's
        // own UI doesn't render the no-avatar fallback for one tick.
        tm.avatar_url = googleAvatar;
      }

      setTeamMember(tm);
      saveCachedTeamMember(tm);
      setValidating(false);
    }
    void validate();
    return () => {
      active = false;
    };
  }, [session, sessionLoading]);

  // 3. Keep the cached row honest (migration 078). The same-user fast path
  // above never touches the database, so without this a deactivated or
  // expired member would stay signed in on their cached row. Re-check on
  // start, when the tab regains focus and every 5 minutes. A network error
  // never signs anyone out; only a definite "no longer a member" does.
  const memberEmail = session?.user?.email?.toLowerCase() ?? null;
  const hasMember = !!teamMember;
  useEffect(() => {
    if (!memberEmail || !hasMember) return;
    let cancelled = false;
    let last = 0;
    async function recheck() {
      if (cancelled || Date.now() - last < 15_000) return;
      last = Date.now();
      const [rowRes, accessRes] = await Promise.all([
        supabase.from('team_members').select('*').eq('email', memberEmail).eq('active', true).maybeSingle(),
        supabase.rpc('my_access'),
      ]);
      if (cancelled || rowRes.error || accessRes.error) return;
      if (!rowRes.data || accessRes.data == null) {
        clearCachedTeamMember();
        setTeamMember(null);
        setError(
          'Your access to the support team has ended or been paused. Ask an owner or admin if that is a mistake.',
        );
        await signOut();
        return;
      }
      const fresh = rowRes.data as TeamMember;
      const prev = teamMemberRef.current;
      if (!prev || JSON.stringify(prev) !== JSON.stringify(fresh)) {
        setTeamMember(fresh);
        saveCachedTeamMember(fresh);
      }
    }
    void recheck();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void recheck();
    };
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(() => void recheck(), 5 * 60_000);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [memberEmail, hasMember]);

  // Combined loading: true until BOTH session is known AND team_members is checked.
  const loading = sessionLoading || validating;

  const value = useMemo<AuthState>(
    () => ({ session, teamMember, loading, error }),
    [session, teamMember, loading, error],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { session, teamMember, loading, error } = useAuth();
  const location = useLocation();

  // Only show the loading splash on FIRST load (when we haven't validated
  // a teamMember yet). Subsequent re-validations (e.g. Supabase token
  // refresh on tab focus) keep the children mounted so scroll positions
  // + composer state + dialog state all survive cleanly. The auth gate
  // is still active under the hood — if the re-validate fails, the
  // user will be bounced to /login by the next branch below.
  if (loading && !teamMember) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        <div className="flex items-center gap-3">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
          Checking sign-in…
        </div>
      </div>
    );
  }

  // Auth-flash guard: when we have a cached teamMember but the Supabase
  // session check is still in flight, the `session` state is null for a
  // tick — and without this guard, the next branch would briefly
  // redirect to /login before the session resolved and bounced the
  // user back. By gating on `!loading`, we render the children
  // optimistically against the cached identity until validation
  // confirms or rejects it. If it rejects (signed out elsewhere,
  // teammate row deactivated, etc.), `teamMember` flips to null in the
  // validate effect and this branch fires cleanly.
  if (loading && teamMember) {
    return <>{children}</>;
  }

  if (!session || !teamMember) {
    const params = new URLSearchParams();
    if (error) params.set('reason', error);
    const search = params.toString();
    // Preserve the full URL (path + query + hash) so post-login can restore exactly where the user was.
    const from = location.pathname + location.search + location.hash;
    return (
      <Navigate
        to={`/login${search ? '?' + search : ''}`}
        replace
        state={{ from }}
      />
    );
  }

  return <>{children}</>;
}
